<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { BriefcaseBusiness, Expand, Minimize2, PencilLine, Sparkles } from 'lucide-vue-next'
import { useToast } from 'primevue/usetoast'

import AIAssistButton from '@/components/atoms/buttons/AIAssistButton.vue'
import AIAssistPopover from '@/components/molecules/ai/AIAssistPopover.vue'
import { useTextAssistantService, type TextAssistantAction } from '@/services/ai/useTextAssistantService'
import { useAiCreditsStore } from '@/stores/useAiCreditsStore'

type AssistantAction = Exclude<TextAssistantAction, 'summarize'>
type FieldType = 'input' | 'textarea'
type PanelPlacement = 'bottom' | 'top' | 'right' | 'left'
type PanelMode = 'absolute' | 'fixed'

interface Props {
  modelValue?: string
  fieldType?: FieldType
  disabled?: boolean
  contextLabel?: string
  minCharsForRewrite?: number
  allowAppend?: boolean
  panelPlacement?: PanelPlacement
  panelMode?: PanelMode
  embedded?: boolean
  title?: string
  panelZIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  fieldType: 'textarea',
  disabled: false,
  contextLabel: 'texto',
  minCharsForRewrite: 30,
  allowAppend: true,
  panelPlacement: 'bottom',
  panelMode: 'absolute',
  embedded: true,
  title: '',
  panelZIndex: 3000,
})

const emit = defineEmits<{
  (e: 'replace', value: string): void
  (e: 'append', value: string): void
  (e: 'copy', value: string): void
  (e: 'error', value: unknown): void
  (e: 'execute', value: { action: AssistantAction; result: string }): void
}>()

const toast = useToast()
const textAssistantService = useTextAssistantService()
const aiCreditsStore = useAiCreditsStore()

const ACTION_LABELS: Record<AssistantAction, string> = {
  improve: 'Mejorar',
  formalize: 'Formalizar',
  develop: 'Desarrollar',
  reduce: 'Reducir',
  generate: 'Ayudame a escribir',
  custom: 'Personalizar',
}

const ACTION_ICONS: Record<AssistantAction, object> = {
  improve: Sparkles,
  formalize: BriefcaseBusiness,
  develop: Expand,
  reduce: Minimize2,
  generate: Sparkles,
  custom: PencilLine,
}

const rootRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const workspaceRef = ref<HTMLElement | null>(null)

const mode = ref<'closed' | 'menu' | 'workspace'>('closed')
const selectedAction = ref<AssistantAction>('generate')
const instruction = ref('')
const targetLanguage = ref('en')
const loading = ref(false)
const result = ref('')
const generatedSourceText = ref('')
const floatingStyle = ref<Record<string, string>>({})

const activeSourceText = computed(() => String(result.value || generatedSourceText.value || '').trim())
const actionSourceText = computed(() => activeSourceText.value || String(props.modelValue || '').trim())
const hasInputText = computed(() => String(props.modelValue || '').trim().length > 0)
const hasResult = computed(() => String(result.value || '').trim().length > 0)
const showComposer = computed(
  () =>
    (selectedAction.value === 'generate' || selectedAction.value === 'custom') &&
    !loading.value &&
    !hasResult.value,
)
const isMenuOpen = computed(() => mode.value === 'menu')
const isWorkspaceOpen = computed(() => mode.value === 'workspace')
const isActive = computed(() => mode.value !== 'closed')

const availableActions = computed<AssistantAction[]>(() => {
  const sourceLength = actionSourceText.value.length
  const baseActions: AssistantAction[] =
    sourceLength >= props.minCharsForRewrite
      ? ['improve', 'formalize', 'develop', 'reduce', 'generate']
      : ['generate']

  const hasSourceContext = hasInputText.value || !!activeSourceText.value

  if (hasSourceContext && !baseActions.includes('custom')) {
    baseActions.splice(Math.max(baseActions.indexOf('generate'), 0), 0, 'custom')
  }

  return baseActions
})

const actionOptions = computed(() =>
  availableActions.value.map((action) => ({
    label: action === 'generate' && !hasInputText.value ? 'Generar' : ACTION_LABELS[action],
    value: action,
  })),
)

const useCompactComposer = computed(() => isWorkspaceOpen.value)

const panelClass = computed(() => {
  if (props.panelMode === 'fixed') return 'fixed'

  const placement =
    props.panelPlacement === 'top'
      ? 'bottom-[calc(100%+0.5rem)] right-0'
      : props.panelPlacement === 'right'
        ? 'left-[calc(100%+0.5rem)] top-0'
        : props.panelPlacement === 'left'
          ? 'right-[calc(100%+0.5rem)] top-0'
          : 'top-[calc(100%+0.5rem)] right-0'

  return `absolute ${placement}`
})

const panelZIndexStyle = computed(() => ({ zIndex: String(props.panelZIndex) }))

const rootClass = computed(() => {
  if (!props.embedded) return 'relative inline-flex'

  return props.fieldType === 'input'
    ? 'absolute right-3 top-1/2 z-20 -translate-y-1/2'
    : 'absolute bottom-3 right-3 z-20'
})

function ensureValidAction() {
  if (!availableActions.value.includes(selectedAction.value)) {
    selectedAction.value = availableActions.value[0] ?? 'generate'
  }
}

function setFloatingPosition() {
  if (props.panelMode !== 'fixed') return
  const trigger = triggerRef.value
  if (!trigger) return

  const rect = trigger.getBoundingClientRect()
  const panel = mode.value === 'menu' ? menuRef.value : workspaceRef.value?.firstElementChild
  const panelRect = panel instanceof HTMLElement ? panel.getBoundingClientRect() : null
  const width = panelRect?.width || (isMenuOpen.value ? 224 : useCompactComposer.value ? 544 : 416)
  const height = panelRect?.height || (isMenuOpen.value ? 112 : showComposer.value ? 96 : result.value ? 216 : 320)
  const gap = 8

  let left =
    props.panelPlacement === 'right'
      ? rect.right + gap
      : props.panelPlacement === 'left'
        ? rect.left - width - gap
        : rect.right - width
  let top =
    props.panelPlacement === 'top'
      ? rect.top - height - gap
      : props.panelPlacement === 'right' || props.panelPlacement === 'left'
        ? rect.top
        : rect.bottom + gap

  left = Math.max(8, Math.min(left, window.innerWidth - width - 8))
  top = Math.max(8, Math.min(top, window.innerHeight - height - 8))

  floatingStyle.value = { left: `${left}px`, top: `${top}px` }
}

function scheduleFloatingPosition() {
  nextTick(() => {
    setFloatingPosition()
    window.requestAnimationFrame(setFloatingPosition)
  })
}

function openMenu() {
  ensureValidAction()
  mode.value = 'menu'
  scheduleFloatingPosition()
}

function openWorkspace() {
  mode.value = 'workspace'
  scheduleFloatingPosition()
}

function closeAll() {
  mode.value = 'closed'
}

function toggleAssistant() {
  if (props.disabled) return
  if (mode.value !== 'closed') {
    closeAll()
    return
  }
  if (hasInputText.value) {
    openMenu()
  } else {
    selectedAction.value = 'generate'
    openWorkspace()
  }
}

async function executeAction() {
  if (loading.value) return

  loading.value = true
  result.value = ''

  try {
    const response = await textAssistantService.executeAction({
      action: selectedAction.value,
      text: actionSourceText.value,
      instruction: instruction.value.trim(),
      domain_code: null,
    })
    result.value = response.result
    generatedSourceText.value = response.result
    emit('execute', { action: selectedAction.value, result: response.result })
    await aiCreditsStore.refreshCredits({ force: true })
    scheduleFloatingPosition()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No se pudo generar el texto con IA.',
      life: 3000,
    })
    emit('error', error)
  } finally {
    loading.value = false
    scheduleFloatingPosition()
  }
}

function handleSelectAction(action: AssistantAction) {
  const currentGeneratedText = String(result.value || generatedSourceText.value || '').trim()
  selectedAction.value = action
  result.value = ''

  if (action === 'custom' && currentGeneratedText) {
    generatedSourceText.value = currentGeneratedText
  }

  openWorkspace()

  if (action !== 'generate' && action !== 'custom') {
    nextTick(() => executeAction())
  }
}

function regenerateResult() {
  if (showComposer.value) return
  void executeAction()
}

async function copyResult() {
  const value = String(result.value || '').trim()
  if (!value) return

  try {
    await navigator.clipboard.writeText(value)
    emit('copy', value)
    toast.add({
      severity: 'success',
      summary: 'Copiado',
      detail: 'Texto copiado con exito.',
      life: 2500,
    })
  } catch (error) {
    emit('error', error)
  }
}

function replaceResult() {
  const value = String(result.value || '').trim()
  if (!value) return
  emit('replace', value)
  closeAll()
}

function appendResult() {
  const value = String(result.value || '').trim()
  if (!value) return
  emit('append', value)
  closeAll()
}

function containsTarget(element: HTMLElement | null, target: Node) {
  return !!(element && element.contains(target))
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target
  if (!(target instanceof Node)) return
  if (containsTarget(rootRef.value, target)) return
  if (containsTarget(menuRef.value, target)) return
  if (containsTarget(workspaceRef.value, target)) return
  closeAll()
}

function handleViewportChange() {
  if (mode.value === 'closed' || props.panelMode !== 'fixed') return
  scheduleFloatingPosition()
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (!String(newValue || '').trim()) {
      result.value = ''
      generatedSourceText.value = ''
      if (selectedAction.value !== 'generate') {
        selectedAction.value = 'generate'
      }
    }
    ensureValidAction()
  },
)

onMounted(() => {
  ensureValidAction()
  document.addEventListener('mousedown', handleClickOutside)
  window.addEventListener('resize', handleViewportChange)
  window.addEventListener('scroll', handleViewportChange, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside)
  window.removeEventListener('resize', handleViewportChange)
  window.removeEventListener('scroll', handleViewportChange, true)
})

defineExpose({
  toggleAssistant,
})
</script>

<template>
  <div ref="rootRef" :class="rootClass">
    <div ref="triggerRef">
      <AIAssistButton
        :disabled="disabled"
        :loading="loading"
        :title="title"
        class="cursor-pointer"
        :active="isActive"
        @click="toggleAssistant"
      />
    </div>

    <Teleport v-if="panelMode === 'fixed' && isMenuOpen" to="body">
      <div
        ref="menuRef"
        class="text-ai-assistant-panel fixed w-56 rounded-2xl border border-[var(--p-surface-200)] bg-white p-3 shadow-2xl dark:border-[var(--p-surface-700)] dark:bg-[var(--p-surface-900)]"
        :style="[floatingStyle, panelZIndexStyle]"
      >
        <div class="space-y-1">
          <button
            v-for="option in actionOptions"
            :key="option.value"
            type="button"
            class="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-xs text-[var(--p-surface-700)] transition hover:bg-[var(--p-surface-100)] dark:text-[var(--p-surface-200)] dark:hover:bg-[var(--p-surface-800)]"
            @click="handleSelectAction(option.value)"
          >
            <component :is="ACTION_ICONS[option.value]" class="h-4 w-4 shrink-0" />
            <span>{{ option.label }}</span>
          </button>
        </div>
      </div>
    </Teleport>

    <div
      v-else-if="isMenuOpen"
      ref="menuRef"
      class="absolute top-[calc(100%+0.5rem)] right-0 w-56 rounded-2xl border border-[var(--p-surface-200)] bg-white p-3 shadow-2xl dark:border-[var(--p-surface-700)] dark:bg-[var(--p-surface-900)]"
      :style="panelZIndexStyle"
    >
      <div class="space-y-1">
        <button
          v-for="option in actionOptions"
          :key="option.value"
          type="button"
          class="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-xs text-[var(--p-surface-700)] transition hover:bg-[var(--p-surface-100)] dark:text-[var(--p-surface-200)] dark:hover:bg-[var(--p-surface-800)]"
          @click="handleSelectAction(option.value)"
        >
          <component :is="ACTION_ICONS[option.value]" class="h-4 w-4 shrink-0" />
          <span>{{ option.label }}</span>
        </button>
      </div>
    </div>

    <Teleport v-if="panelMode === 'fixed' && isWorkspaceOpen" to="body">
      <div ref="workspaceRef" class="text-ai-assistant-panel">
        <AIAssistPopover
          :is-open="isWorkspaceOpen"
          :loading="loading"
          :result="result"
          :model-value="modelValue"
          :source-text="actionSourceText"
          :selected-action="selectedAction"
          :instruction="instruction"
          :target-language="targetLanguage"
          :actions="availableActions"
          :can-append="allowAppend"
          :panel-class="panelClass"
          :show-composer="showComposer"
          :compact-composer="useCompactComposer"
          :style="[floatingStyle, panelZIndexStyle]"
          @close="closeAll"
          @update:instruction="instruction = $event"
          @update:target-language="targetLanguage = $event"
          @select-action="handleSelectAction"
          @execute="executeAction"
          @regenerate="regenerateResult"
          @replace="replaceResult"
          @append="appendResult"
          @copy="copyResult"
        />
      </div>
    </Teleport>

    <div v-else-if="isWorkspaceOpen" ref="workspaceRef">
      <AIAssistPopover
        :is-open="isWorkspaceOpen"
        :loading="loading"
        :result="result"
        :model-value="modelValue"
        :source-text="actionSourceText"
        :selected-action="selectedAction"
        :instruction="instruction"
        :target-language="targetLanguage"
        :actions="availableActions"
        :can-append="allowAppend"
        :panel-class="panelClass"
        :show-composer="showComposer"
        :compact-composer="useCompactComposer"
        :style="panelZIndexStyle"
        @close="closeAll"
        @update:instruction="instruction = $event"
        @update:target-language="targetLanguage = $event"
        @select-action="handleSelectAction"
        @execute="executeAction"
        @regenerate="regenerateResult"
        @replace="replaceResult"
        @append="appendResult"
        @copy="copyResult"
      />
    </div>
  </div>
</template>
